/*
Copyright 2023, 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  type ComponentType,
  type JSX,
  memo,
  type RefObject,
  useEffect,
  useRef,
} from "react";
import { type EventTypes, type Handler, useDrag } from "@use-gesture/react";
import { type SpringValue } from "@react-spring/web";
import classNames from "classnames";

import { type TileProps } from "./Grid";
import styles from "./TileWrapper.module.css";

interface Props<M, R extends HTMLElement> {
  id: string;
  onDrag: RefObject<
    (
      tileId: string,
      state: Parameters<Handler<"drag", EventTypes["drag"]>>[0],
    ) => void
  > | null;
  /**
   * The width this tile will have once its animations have settled.
   */
  targetWidth: number;
  /**
   * The width this tile will have once its animations have settled.
   */
  targetHeight: number;
  model: M;
  Tile: ComponentType<TileProps<M, R>>;
  opacity: SpringValue<number>;
  scale: SpringValue<number>;
  zIndex: SpringValue<number>;
  x: SpringValue<number>;
  y: SpringValue<number>;
  width: SpringValue<number>;
  height: SpringValue<number>;
}

const TileWrapper_ = memo(
  <M, R extends HTMLElement>({
    id,
    onDrag,
    targetWidth,
    targetHeight,
    model,
    Tile,
    opacity,
    scale,
    zIndex,
    x,
    y,
    width,
    height,
  }: Props<M, R>) => {
    const ref = useRef<R | null>(null);
    // Whether the gesture that just ended was a drag rather than a tap
    const dragged = useRef(false);

    useDrag(
      (state) => {
        // A new gesture forgets any drag whose click never arrived
        if (state.first) dragged.current = false;
        if (state.last && !state.tap) dragged.current = true;
        onDrag?.current!(id, state);
      },
      {
        target: ref,
        enabled: onDrag !== null,
        // Ignore movements too small to be intentional (the same threshold
        // filterTaps would apply)
        threshold: 3,
        // On touchscreens, the tile must be held for a moment before it can
        // be dragged; moving straight away scrolls as normal.
        preventScroll: 250,
        preventScrollAxis: "xy",
        // We deliberately don't use filterTaps: together with preventScroll
        // it swallows the clicks of buttons within tiles (like the 'switch
        // camera' button) on mobile, because a short tap never reaches the
        // point where use-gesture works out that it was a tap.
        // https://github.com/pmndrs/use-gesture/issues/593
        // Instead we swallow the click that follows a drag ourselves, below.
      },
    );

    useEffect(() => {
      const element = ref.current;
      if (element === null) return;
      const onClick = (e: MouseEvent): void => {
        if (dragged.current) {
          dragged.current = false;
          e.preventDefault();
          e.stopPropagation();
        }
      };
      element.addEventListener("click", onClick, true);
      return (): void => element.removeEventListener("click", onClick, true);
    }, []);

    return (
      <Tile
        ref={ref}
        className={classNames(styles.tile, { [styles.draggable]: onDrag })}
        style={{
          opacity,
          scale,
          zIndex,
          x,
          y,
          width,
          height,
        }}
        targetWidth={targetWidth}
        targetHeight={targetHeight}
        model={model}
      />
    );
  },
);

TileWrapper_.displayName = "TileWrapper";

/**
 * A wrapper around a tile in a video grid. This component exists to decouple
 * child components from the grid.
 */
// We pretend this component is a simple function rather than a
// NamedExoticComponent, because that's the only way we can fit in a type
// parameter
export const TileWrapper = TileWrapper_ as <M, R extends HTMLElement>(
  props: Props<M, R>,
) => JSX.Element;
